import type { ContactSubmissionContext } from './types';

export type PostContactFlowType = 'saved-search' | 'waitlist-success' | 'multi-contact' | 'success';

export type PostContactFlowTitleKey =
  | 'postContactSavedSearchTitle'
  | 'successTitleWaitlist'
  | 'postContactMultiContactTitle'
  | 'successTitleContact'
  | 'successTitleTour';

export type PostContactFlowDefinition = {
  type: PostContactFlowType;
  label: string;
  summary: string;
  titleKey: PostContactFlowTitleKey;
  enabled?: boolean;
  fallback?: boolean;
  shouldRender?: (context: ContactSubmissionContext) => boolean;
};

export const postContactFlowRegistry: Array<PostContactFlowDefinition> = [
  {
    type: 'saved-search',
    label: 'Saved search',
    summary: 'Offer eligible renters a saved search and listing alerts after contact.',
    titleKey: 'postContactSavedSearchTitle',
    enabled: true,
    shouldRender: (context) => context.shouldShowSavedSearch && context.savedSearchContext !== null,
  },
  {
    type: 'waitlist-success',
    label: 'Waitlist success',
    summary: 'Confirm waitlist joins and show a small preview of follow-up listings.',
    titleKey: 'successTitleWaitlist',
    enabled: true,
    shouldRender: (context) => context.isWaitlist && context.similarListings.length > 0,
  },
  {
    type: 'multi-contact',
    label: 'Multi-contact',
    summary: 'Let renters contact additional properties with the same message.',
    titleKey: 'postContactMultiContactTitle',
    enabled: true,
    shouldRender: (context) => !context.isWaitlist && context.similarListings.length > 0,
  },
  {
    type: 'success',
    label: 'Success',
    summary: 'Fallback confirmation when no richer post-contact flow applies.',
    titleKey: 'successTitleContact',
    enabled: true,
    fallback: true,
  },
];

export function getPostContactFlowCatalog() {
  return postContactFlowRegistry.map(({ type, label, summary, enabled }) => ({
    type,
    label,
    summary,
    enabled: enabled !== false,
  }));
}

export function getActivePostContactFlows(context: ContactSubmissionContext | null) {
  if (!context) return [];

  const enabledFlows = postContactFlowRegistry.filter((flow) => flow.enabled !== false);
  const primaryFlows = enabledFlows.filter(
    (flow) => !flow.fallback && (!flow.shouldRender || flow.shouldRender(context)),
  );

  if (primaryFlows.length > 0) {
    return primaryFlows;
  }

  return enabledFlows.filter((flow) => flow.fallback);
}

export function postContactFlowShowsSuccessConfirmation(flow: PostContactFlowDefinition): boolean {
  return flow.type === 'success' || flow.type === 'waitlist-success';
}

export function getPostContactFlowTitleKey(
  flow: PostContactFlowDefinition,
  context: ContactSubmissionContext,
): PostContactFlowTitleKey {
  if (flow.type !== 'success') {
    return flow.titleKey;
  }

  if (context.mode === 'tour') {
    return 'successTitleTour';
  }

  if (context.isWaitlist) {
    return 'successTitleWaitlist';
  }

  return flow.titleKey;
}
